import { useState, useEffect } from 'react';
import { attachmentFieldsFromRecord } from '../../utils/memberAttachments';
import {
  getFirestore, collection, query, orderBy, onSnapshot,
  doc, updateDoc, addDoc, setDoc, serverTimestamp, getDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';

const STATUS_CONFIG = {
  pending:        { label: 'PENDING',        color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  },
  approved:       { label: 'APPROVED',       color: '#10b981', bg: 'rgba(16,185,129,0.12)'  },
  rejected:       { label: 'REJECTED',       color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
  info_requested: { label: 'INFO REQUESTED', color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)'  },
  info_responded: { label: 'INFO RECEIVED',  color: '#0e9cc0', bg: 'rgba(14,156,192,0.12)'  },
  denied:         { label: 'REJECTED',       color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
};

const PORTAL_COLORS = {
  mp: '#6366f1', embedded: '#f59e0b', system: '#10b981',
  ops: '#e11d48', pe: '#8b5cf6', obc: '#00FFB2',
  experimental: '#06b6d4', finance: '#34d399',
  admin: '#0e9cc0',
};

const ROLE_OPTS = [
  { id: 'mp',           label: 'Mission Planning'    },
  { id: 'embedded',     label: 'Embedded Systems'    },
  { id: 'system',       label: 'Systems Engineering' },
  { id: 'ops',          label: 'Operations'          },
  { id: 'pe',           label: 'Prompt Engineering'  },
  { id: 'obc',          label: 'OBC Development'     },
  { id: 'experimental', label: 'Experimental'        },
  { id: 'finance',      label: 'Finance'             },
  { id: 'admin',        label: 'Admin'               },
];

const fmt = ts => ts?.toDate?.()?.toLocaleString('en-IN', {
  day: 'numeric', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
}) || '—';

export default function AccessRequestsTab() {
  const db   = getFirestore(getApp());
  const auth = getAuth(getApp());

  const [adminUid,  setAdminUid]  = useState('');
  const [adminName, setAdminName] = useState('Admin');
  const [requests,  setRequests]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('pending');
  const [selected,  setSelected]  = useState(null);
  const [busy,      setBusy]      = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [approvedRole, setApprovedRole] = useState('mp');

  // Load current admin user
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    setAdminUid(uid);
    getDoc(doc(db, 'authorized_users', uid))
      .then(s => { if (s.exists()) setAdminName(s.data().name || 'Admin'); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Live query — order by submittedAt, fall back gracefully
  useEffect(() => {
    const q = query(collection(db, 'access_requests'), orderBy('submittedAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => {
      // Fallback: try requestedAt for legacy docs
      const q2 = query(collection(db, 'access_requests'), orderBy('requestedAt', 'desc'));
      onSnapshot(q2, snap => {
        setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }, () => setLoading(false));
    });
    return unsub;
  }, [db]);

  const counts = {
    pending:        requests.filter(r => r.status === 'pending').length,
    info_responded: requests.filter(r => r.status === 'info_responded').length,
    approved:       requests.filter(r => r.status === 'approved').length,
    rejected:       requests.filter(r => r.status === 'rejected' || r.status === 'denied').length,
  };

  const filtered = filter === 'all'
    ? requests
    : filter === 'rejected'
    ? requests.filter(r => r.status === 'rejected' || r.status === 'denied')
    : requests.filter(r => r.status === filter);

  function openRequest(req) {
    setSelected(req);
    setReviewNote(req.reviewNote || '');
    setApprovedRole(req.approvedRole || req.role || 'mp');
  }

  // ── APPROVE ──────────────────────────────────────────────────────────────────
  async function handleApprove() {
    if (!selected) return;
    setBusy(true);
    try {
      const roleLabel = ROLE_OPTS.find(r => r.id === approvedRole)?.label || approvedRole;

      await setDoc(doc(db, 'authorized_users', selected.uid), {
        uid:        selected.uid,
        name:       selected.name,
        email:      selected.email,
        role:       approvedRole,
        active:     true,
        approvedAt: serverTimestamp(),
        approvedBy: adminName,
        theme:      'dark',
        leaveBalance: { casual: 12, sick: 10, earned: 21, comp_off: 0, wfh: 5 },
        leaveUsed:    { casual: 0,  sick: 0,  earned: 0,  comp_off: 0, wfh: 0  },
        shiftConfig: {
          shiftName: '09:30-18:30', shiftStart: '09:30',
          shiftEnd: '18:30', companyHours: 8,
        },
        ...attachmentFieldsFromRecord(selected),
      });

      await updateDoc(doc(db, 'access_requests', selected.id), {
        status:           'approved',
        approvedRole:     approvedRole,
        reviewedBy:       adminName,
        reviewedByUid:    adminUid,
        reviewedAt:       serverTimestamp(),
        reviewNote:       reviewNote || null,
        notifiedApplicant: false,
      });

      await addDoc(collection(db, 'notifications_queue'), {
        eventType:  'ACCESS_APPROVED',
        sourceId:   selected.id,
        sourceType: 'access_request',
        payload: {
          applicantUid:  selected.uid,
          applicantName: selected.name,
          roleLabel,
          approvedRole,
          reviewNote:    reviewNote || null,
        },
        actorUid:  adminUid,
        actorName: adminName,
        processed: false,
        createdAt: serverTimestamp(),
      });

      setSelected(null);
    } catch (err) {
      console.error('[AccessRequestsTab approve]', err);
    } finally {
      setBusy(false);
    }
  }

  // ── REJECT ───────────────────────────────────────────────────────────────────
  async function handleReject() {
    if (!selected) return;
    if (!reviewNote.trim()) {
      alert('Please provide a rejection reason so the applicant understands why.');
      return;
    }
    setBusy(true);
    try {
      await updateDoc(doc(db, 'access_requests', selected.id), {
        status:           'rejected',
        reviewedBy:       adminName,
        reviewedByUid:    adminUid,
        reviewedAt:       serverTimestamp(),
        reviewNote:       reviewNote.trim(),
        notifiedApplicant: false,
      });

      await addDoc(collection(db, 'notifications_queue'), {
        eventType:  'ACCESS_REJECTED',
        sourceId:   selected.id,
        sourceType: 'access_request',
        payload: {
          applicantUid:  selected.uid,
          applicantName: selected.name,
          note: reviewNote.trim(),
        },
        actorUid:  adminUid,
        actorName: adminName,
        processed: false,
        createdAt: serverTimestamp(),
      });

      setSelected(null);
    } catch (err) {
      console.error('[AccessRequestsTab reject]', err);
    } finally {
      setBusy(false);
    }
  }

  // ── REQUEST MORE INFO ─────────────────────────────────────────────────────────
  async function handleRequestInfo() {
    if (!selected) return;
    if (!reviewNote.trim()) {
      alert('Please specify what information you need from the applicant.');
      return;
    }
    setBusy(true);
    try {
      await updateDoc(doc(db, 'access_requests', selected.id), {
        status:        'info_requested',
        reviewedBy:    adminName,
        reviewedByUid: adminUid,
        reviewedAt:    serverTimestamp(),
        reviewNote:    reviewNote.trim(),
      });

      await addDoc(collection(db, 'notifications_queue'), {
        eventType:  'ACCESS_INFO_REQUESTED',
        sourceId:   selected.id,
        sourceType: 'access_request',
        payload: {
          applicantUid:  selected.uid,
          applicantName: selected.name,
          question: reviewNote.trim(),
        },
        actorUid:  adminUid,
        actorName: adminName,
        processed: false,
        createdAt: serverTimestamp(),
      });

      setSelected(null);
    } catch (err) {
      console.error('[AccessRequestsTab requestInfo]', err);
    } finally {
      setBusy(false);
    }
  }

  const filterTabs = [
    { id: 'pending',        label: 'Pending',   badge: counts.pending        },
    { id: 'info_responded', label: 'Responded', badge: counts.info_responded },
    { id: 'approved',       label: 'Approved',  badge: null                  },
    { id: 'rejected',       label: 'Rejected',  badge: null                  },
    { id: 'all',            label: 'All',       badge: requests.length       },
  ];

  const canAct = selected && (selected.status === 'pending' || selected.status === 'info_responded');

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 500 }}>

      {/* ── LEFT: list ─────────────────────────────────────────────────────── */}
      <div style={{
        width: selected ? 300 : '100%',
        flexShrink: 0,
        borderRight: selected ? '1px solid #1e2636' : 'none',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s',
      }}>

        {/* Filter pills */}
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #1e2636' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {filterTabs.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', borderRadius: 6,
                  fontSize: 9, letterSpacing: 1, cursor: 'pointer',
                  fontFamily: 'monospace',
                  background: filter === f.id ? '#0e9cc020' : 'transparent',
                  border: `1px solid ${filter === f.id ? '#0e9cc055' : '#1e2636'}`,
                  color: filter === f.id ? '#0e9cc0' : '#445566',
                }}
              >
                {f.label.toUpperCase()}
                {f.badge != null && f.badge > 0 && (
                  <span style={{
                    minWidth: 15, height: 15, borderRadius: 8, padding: '0 3px',
                    background: f.id === 'pending' ? '#f59e0b' : f.id === 'info_responded' ? '#0e9cc0' : '#445566',
                    color: '#000', fontSize: 8, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {f.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Request rows */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            [1, 2, 3].map(i => (
              <div key={i} style={{ padding: '12px 14px', borderBottom: '1px solid #1e263620' }}>
                <div style={{ height: 11, borderRadius: 4, background: '#1e2636', width: '55%', marginBottom: 6 }} />
                <div style={{ height: 9, borderRadius: 3, background: '#1e2636', width: '38%' }} />
              </div>
            ))
          ) : filtered.length === 0 ? (
            <div style={{
              padding: '40px 14px', textAlign: 'center',
              fontFamily: 'monospace', fontSize: 10, color: '#334455',
            }}>
              No {filter === 'all' ? '' : filter} requests.
            </div>
          ) : filtered.map(req => {
            const sc    = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
            const color = PORTAL_COLORS[req.role] || '#0e9cc0';
            const isNew = req.status === 'pending' || req.status === 'info_responded';
            return (
              <div
                key={req.id}
                onClick={() => openRequest(req)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 14px',
                  borderBottom: '1px solid #1e263622',
                  borderLeft: selected?.id === req.id
                    ? '3px solid #0e9cc0' : '3px solid transparent',
                  background: selected?.id === req.id ? '#0e9cc010' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: color + '20', border: `1px solid ${color}44`,
                  color, fontSize: 10, fontWeight: 700, fontFamily: 'monospace',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative',
                }}>
                  {(req.name || '?').substring(0, 2).toUpperCase()}
                  {isNew && (
                    <div style={{
                      position: 'absolute', top: -2, right: -2,
                      width: 8, height: 8, borderRadius: '50%',
                      background: req.status === 'info_responded' ? '#0e9cc0' : '#f59e0b',
                      border: '1.5px solid #0c0f18',
                    }} />
                  )}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 600, color: '#dde9f0',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    marginBottom: 2,
                  }}>
                    {req.name}
                  </div>
                  <div style={{
                    fontSize: 9, color: '#445566',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    marginBottom: 4,
                  }}>
                    {req.email}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 8, padding: '1px 6px', borderRadius: 3,
                      background: color + '18', color,
                      fontFamily: 'monospace',
                    }}>
                      {req.roleLabel || req.team || req.role}
                    </span>
                    <span style={{
                      fontSize: 8, padding: '1px 6px', borderRadius: 3,
                      background: sc.bg, color: sc.color,
                      fontFamily: 'monospace', fontWeight: 600,
                    }}>
                      {sc.label}
                    </span>
                  </div>
                  <div style={{
                    fontSize: 8, color: '#2a3a50', fontFamily: 'monospace', marginTop: 3,
                  }}>
                    {fmt(req.submittedAt || req.requestedAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT: detail panel ─────────────────────────────────────────────── */}
      {selected && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

          {/* Panel header */}
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid #1e2636',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#dde9f0' }}>
              {selected.name}
            </div>
            <button
              onClick={() => setSelected(null)}
              style={{ background: 'none', border: 'none', color: '#445566', cursor: 'pointer', fontSize: 16 }}
            >
              ✕
            </button>
          </div>

          {/* Detail body */}
          <div style={{ padding: '14px 16px', flex: 1 }}>

            {/* Info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
              {[
                { label: 'EMAIL',      value: selected.email },
                { label: 'PHONE',      value: selected.phone || '—' },
                { label: 'REQUESTED',  value: selected.roleLabel || selected.team || selected.role },
                { label: 'STATUS',     value: (STATUS_CONFIG[selected.status] || STATUS_CONFIG.pending).label },
                { label: 'SUBMITTED',  value: fmt(selected.submittedAt || selected.requestedAt) },
                { label: 'REVIEWED',   value: selected.reviewedAt
                  ? `${fmt(selected.reviewedAt)} by ${selected.reviewedBy}`
                  : '—'
                },
              ].map(({ label, value }) => (
                <div key={label} style={{
                  background: '#0c0f18', border: '1px solid #1e2636',
                  borderRadius: 6, padding: '7px 10px',
                }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#334455', letterSpacing: 1, marginBottom: 3 }}>
                    {label}
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#7b8fa8', wordBreak: 'break-all' }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {/* Reason */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#334455', letterSpacing: 1, marginBottom: 5 }}>
                REASON FOR ACCESS
              </div>
              <div style={{
                background: '#0c0f18', border: '1px solid #1e2636', borderRadius: 6,
                padding: '9px 12px', fontSize: 11, color: '#7b8fa8', lineHeight: 1.6,
              }}>
                {selected.reason}
              </div>
            </div>

            {/* LinkedIn */}
            {(selected.linkedinUrl || selected.linkedin) && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#334455', letterSpacing: 1, marginBottom: 5 }}>
                  LINKEDIN
                </div>
                <a
                  href={selected.linkedinUrl || selected.linkedin}
                  target="_blank" rel="noreferrer"
                  style={{ fontFamily: 'monospace', fontSize: 10, color: '#0e9cc0' }}
                >
                  {selected.linkedinUrl || selected.linkedin}
                </a>
              </div>
            )}

            {/* Referral */}
            {selected.referral && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#334455', letterSpacing: 1, marginBottom: 5 }}>
                  REFERRAL / SOURCE
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#7b8fa8' }}>
                  {selected.referral}
                </div>
              </div>
            )}

            {/* Info response from applicant */}
            {selected.infoResponse && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#8b5cf6', letterSpacing: 1, marginBottom: 5 }}>
                  APPLICANT RESPONSE
                </div>
                <div style={{
                  background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)',
                  borderRadius: 6, padding: '9px 12px', fontSize: 11, color: '#7b8fa8', lineHeight: 1.6,
                }}>
                  {selected.infoResponse}
                </div>
                {selected.infoRespondedAt && (
                  <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#334455', marginTop: 4 }}>
                    Responded {fmt(selected.infoRespondedAt)}
                  </div>
                )}
              </div>
            )}

            {/* Admin's note if present */}
            {selected.reviewNote && !canAct && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#334455', letterSpacing: 1, marginBottom: 5 }}>
                  ADMIN NOTE
                </div>
                <div style={{
                  background: '#0c0f18', border: '1px solid #1e2636',
                  borderRadius: 6, padding: '9px 12px', fontSize: 11, color: '#7b8fa8', lineHeight: 1.6,
                }}>
                  {selected.reviewNote}
                </div>
              </div>
            )}

            {/* ── ACTION PANEL — pending or info_responded only ── */}
            {canAct && (
              <div style={{ marginTop: 4 }}>
                {/* Role assignment */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#334455', letterSpacing: 1, marginBottom: 5 }}>
                    ASSIGN ROLE
                  </div>
                  <select
                    value={approvedRole}
                    onChange={e => setApprovedRole(e.target.value)}
                    style={{
                      width: '100%', background: '#0b0e14', border: '1px solid #1e2636',
                      color: '#dde9f0', padding: '8px 12px', fontFamily: 'monospace',
                      fontSize: 11, borderRadius: 4, outline: 'none',
                    }}
                  >
                    {ROLE_OPTS.map(r => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {/* Review note */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#334455', letterSpacing: 1, marginBottom: 5 }}>
                    NOTE (required for reject / info request)
                  </div>
                  <textarea
                    value={reviewNote}
                    onChange={e => setReviewNote(e.target.value)}
                    placeholder="Welcome message, rejection reason, or question for applicant…"
                    rows={3}
                    style={{
                      width: '100%', background: '#0b0e14', border: '1px solid #1e2636',
                      color: '#dde9f0', padding: '8px 12px', fontFamily: 'monospace',
                      fontSize: 11, resize: 'none', lineHeight: 1.5, outline: 'none',
                      borderRadius: 4, boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 7 }}>
                  <button
                    onClick={handleApprove}
                    disabled={busy}
                    style={{
                      flex: 1, padding: '9px 0', borderRadius: 6,
                      background: '#10b98120', border: '1px solid #10b98155',
                      color: '#10b981', fontFamily: 'monospace', fontSize: 10,
                      letterSpacing: 1, cursor: busy ? 'wait' : 'pointer',
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    ✓ APPROVE
                  </button>
                  <button
                    onClick={handleRequestInfo}
                    disabled={busy}
                    style={{
                      flex: 1, padding: '9px 0', borderRadius: 6,
                      background: '#8b5cf620', border: '1px solid #8b5cf655',
                      color: '#8b5cf6', fontFamily: 'monospace', fontSize: 10,
                      letterSpacing: 1, cursor: busy ? 'wait' : 'pointer',
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    ? INFO
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={busy}
                    style={{
                      flex: 1, padding: '9px 0', borderRadius: 6,
                      background: '#ef444420', border: '1px solid #ef444455',
                      color: '#ef4444', fontFamily: 'monospace', fontSize: 10,
                      letterSpacing: 1, cursor: busy ? 'wait' : 'pointer',
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    ✕ REJECT
                  </button>
                </div>
              </div>
            )}

            {/* Already resolved */}
            {!canAct && (selected.status === 'approved' || selected.status === 'rejected' || selected.status === 'denied') && (
              <div style={{
                padding: '10px 12px', borderRadius: 6,
                background: selected.status === 'approved'
                  ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
                border: `1px solid ${selected.status === 'approved'
                  ? 'rgba(16,185,129,0.30)' : 'rgba(239,68,68,0.30)'}`,
                fontFamily: 'monospace', fontSize: 10,
                color: selected.status === 'approved' ? '#10b981' : '#ef4444',
              }}>
                {selected.status === 'approved' ? '✓ Approved' : '✕ Rejected'} by {selected.reviewedBy}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
